// PLAN-domain-classifier-iter2 (2026-05-11): v2 用 permalink ボタン + Public 強調 badge
// spec/page-cockpit.md §5.3.1 permalink コピー仕様の v2 反映
//
// - private: 標準 icon、トースト "Permalink copied (要ログインで閲覧可)"
// - public-*: [外部共有可能] badge 強調、トースト "Permalink copied — 外部共有可能なリンクです"
// - permalink URL: Cockpit ドメイン配下 `/v/{uuid}` (Pages Functions が Facade にプロキシ)
import { useCallback } from 'react'
import { useToast } from './common/Toast'

interface Props {
  id: string
  visibility?: string | null
  /** 完全 UUID を inline 表示するか (AI に渡す用途) */
  showId?: boolean
}

export function PermalinkButton({ id, visibility, showId = false }: Props) {
  const { show } = useToast()
  const isPublic = visibility === 'public-searchable' || visibility === 'public-unsearchable'
  const permalink = `${window.location.origin}/v/${id}`

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(permalink)
      show(isPublic ? 'Permalink copied — 外部共有可能なリンクです' : 'Permalink copied (要ログインで閲覧可)', { kind: isPublic ? 'info' : 'success' })
    } catch {
      show('コピーに失敗しました', { kind: 'error' })
    }
  }, [permalink, isPublic, show])

  const copyId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(id)
      show('ID copied', { kind: 'success' })
    } catch {
      show('コピーに失敗しました', { kind: 'error' })
    }
  }, [id, show])

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={copyLink}
        title={`Permalink: ${permalink}`}
        aria-label="Permalink をコピー"
        className="text-xs px-2 py-1 rounded border border-border-subtle text-text-muted hover:text-text-base hover:border-border-strong transition inline-flex items-center gap-1"
      >
        <span aria-hidden>🔗</span>
        <span className="hidden sm:inline">Link</span>
      </button>
      {showId && (
        <button
          type="button"
          onClick={copyId}
          title={`Full ID: ${id} (click to copy)`}
          className="text-[10px] font-mono px-1.5 py-1 rounded border border-border-subtle text-text-subtle hover:text-text-base hover:border-border-strong transition"
        >
          {id.slice(0, 8)}…
        </button>
      )}
      {isPublic && (
        <span
          className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded"
          style={{
            backgroundColor: 'color-mix(in srgb, var(--state-warning) 18%, transparent)',
            color: 'var(--state-warning)',
            border: '1px solid color-mix(in srgb, var(--state-warning) 40%, transparent)',
          }}
          title="このリンクは認証なしで外部から閲覧できます"
        >
          Public
        </span>
      )}
    </div>
  )
}
